import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {InputManager, KeyEventType, KeyType} from '../classes/InputManager.js';

enum KeyPressState {
    None,
    Pressed,
    Down,
    StayDown,
    Up,
}

type KeyStates = {
    [key in KeyType]: KeyPressState;
};

export class KeyboardController extends Component {
    static TypeName = 'keyboard-controller';

    private _inputManager: InputManager;

    private _upCodes = ['KeyW', 'ArrowUp', 'Numpad8'];
    private _downCodes = ['KeyS', 'ArrowDown', 'Numpad2'];
    private _leftCodes = ['KeyA', 'ArrowLeft', 'Numpad4'];
    private _rightCodes = ['KeyD', 'ArrowRight', 'Numpad6'];
    private _spaceCodes = ['Space'];
    private _escapeCodes = ['Escape'];

    private _keyboardMap = new Map<KeyType, string[]>([
        [KeyType.Down, this._downCodes],
        [KeyType.Left, this._leftCodes],
        [KeyType.Right, this._rightCodes],
        [KeyType.Button1, this._spaceCodes],
        [KeyType.Back, this._escapeCodes],
    ]);

    private _pressedKeys: {[key in KeyType]?: KeyPressState} = {};

    start() {
        this._inputManager = InputManager.instance;
    }

    onActivate(): void {
        window.addEventListener('keydown', this._onKeyDown);
        window.addEventListener('keyup', this._onKeyUp);
    }

    onDeactivate(): void {
        window.removeEventListener('keydown', this._onKeyDown);
        window.removeEventListener('keyup', this._onKeyUp);
    }
    update(dt: number) {
        let toBeDeleted = [];
        Object.entries(this._pressedKeys).forEach(([key, value]) => {
            if (value === KeyPressState.Pressed) {
                const typedKey = key as unknown as KeyType;
                // trigger
                this._inputManager.recordKey(+typedKey, KeyEventType.Triggered);
                // set to down
                this._pressedKeys[typedKey] = KeyPressState.Down;
            } else if (value === KeyPressState.Down) {
                const typedKey = key as unknown as KeyType;
                // trigger
                this._inputManager.recordKey(+typedKey, KeyEventType.Pressed);
                // set to down
                this._pressedKeys[typedKey] = KeyPressState.StayDown;
            } else if (value === KeyPressState.Up) {
                const typedKey = key as unknown as KeyType;
                // trigger
                this._inputManager.recordKey(+typedKey, KeyEventType.Released);
                toBeDeleted.push(typedKey);
                this._pressedKeys[typedKey] = KeyPressState.None;
            }
        });

        if (toBeDeleted.length > 0) {
            toBeDeleted.forEach((key) => {
                delete this._pressedKeys[key];
            });
        }
    }

    private _getTypeFromCode(code: string): KeyType | undefined {
        for (const [type, codes] of this._keyboardMap) {
            if (codes.includes(code)) {
                return type;
            }
        }
    }

    private _onKeyDown = (event: KeyboardEvent) => {
        const t: KeyType = this._getTypeFromCode(event.code);
        if (t === undefined || this._pressedKeys[t]) {
            // key was already pressed
            return;
        }
        this._pressedKeys[t] = KeyPressState.Pressed;
    };

    private _onKeyUp = (event: KeyboardEvent) => {
        const t: KeyType = this._getTypeFromCode(event.code);
        if (t && this._pressedKeys[t] && this._pressedKeys[t] != KeyPressState.Up) {
            this._pressedKeys[t] = KeyPressState.Up;
        }
    };
}
